import React, { Fragment } from "react";

import {
  Box,
  FormControl,
  Grid,
  Switch,
  TextField,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Calendar as CalendarIcon, Clock as ClockIcon } from "react-feather";
import { KeyboardDatePicker } from "@material-ui/pickers";

import TimePicker from "@ds-developer/time-picker";

import { UnavailableFormFieldsProps } from "../../types";
import { dateTimeRangeIsValid } from "../../helpers";
import RepeatSelect from "@hubcapp/components/Calendar/components/RepeatSelect";

const useStyles = makeStyles(() => ({
  pickerInput: {
    paddingRight: 0,
  },
}));

const UnavailableFormFields: React.FC<UnavailableFormFieldsProps> = ({
  allDay,
  handleAllDayChange,
  startDate,
  handleStartDateChange,
  handleStartDateClick,
  startTime,
  handleStartTimeChange,
  handleStartTimeClick,
  endDate,
  handleEndDateChange,
  handleEndDateClick,
  endTime,
  handleEndTimeChange,
  handleEndTimeClick,
  values,
  handleChange,
  handleRepeatFocus,
  handleNotesClick,
  errors,
  touched,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMdScreen = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true,
  });
  const rangeIsValid = dateTimeRangeIsValid(
    startDate,
    startTime,
    endDate,
    endTime,
    values.type,
  );

  return (
    <Fragment>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
      >
        <Typography>All day?</Typography>
        <Switch
          disabled={!startDate}
          checked={allDay}
          onChange={handleAllDayChange}
          name="allDay"
          color="primary"
        />
      </Box>

      <Box marginBottom={1}>
        <Grid container spacing={isMdScreen ? 5 : 2}>
          <Grid item xs={6}>
            <KeyboardDatePicker
              inputVariant="outlined"
              name="startDate"
              label="Start date"
              value={startDate}
              onChange={handleStartDateChange}
              onClick={handleStartDateClick}
              placeholder="dd/mm/yyyy"
              format="dd/MM/yyyy"
              inputProps={{
                inputMode: "decimal",
                "data-test": "dob-picker-input",
              }}
              InputProps={{
                classes: { root: classes.pickerInput },
              }}
              fullWidth
              keyboardIcon={<CalendarIcon />}
              error={Boolean(
                touched.startDateTime && allDay && errors.startDateTime,
              )}
              helperText={
                Boolean(
                  touched.startDateTime && allDay && errors.startDateTime,
                ) && errors.startDateTime
              }
            />
          </Grid>

          <Grid item xs={6}>
            <KeyboardDatePicker
              inputVariant="outlined"
              name="endDate"
              label="End date"
              value={endDate}
              onChange={handleEndDateChange}
              onClick={handleEndDateClick}
              placeholder="dd/mm/yyyy"
              format="dd/MM/yyyy"
              inputProps={{
                inputMode: "decimal",
                "data-test": "dob-picker-input",
              }}
              disabled={Boolean(values.repeat)}
              InputProps={{
                classes: { root: classes.pickerInput },
              }}
              minDate={startDate}
              minDateMessage="End date should not be before start date"
              fullWidth
              keyboardIcon={<CalendarIcon />}
              error={
                touched.endDateTime && Boolean(allDay && errors.endDateTime)
              }
              helperText={
                Boolean(touched.endDateTime && allDay && errors.endDateTime) &&
                errors.endDateTime
              }
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={isMdScreen ? 5 : 2}>
        <Grid item xs={6}>
          <TimePicker
            name="startTime"
            label="Start time"
            value={startTime || undefined}
            minutesStep={15}
            onChange={handleStartTimeChange}
            onClick={handleStartTimeClick}
            icon={<ClockIcon />}
            inputDataTest="unavailable-start-time-picker-input"
            isValid={
              !Boolean(
                touched.startDateTime &&
                  !allDay &&
                  errors.startDateTime &&
                  startTime,
              )
            }
            helperText={
              (Boolean(
                touched.startDateTime &&
                  !allDay &&
                  errors.startDateTime &&
                  startTime,
              ) &&
                errors.startDateTime?.[0]) ||
              ""
            }
            disabled={allDay}
          />
        </Grid>

        <Grid item xs={6}>
          <TimePicker
            name="endTime"
            label="End time"
            value={endTime || undefined}
            minutesStep={15}
            onChange={handleEndTimeChange}
            onClick={handleEndTimeClick}
            icon={<ClockIcon />}
            inputDataTest="unavailable-end-time-picker-input"
            isValid={!Boolean(endTime && !rangeIsValid.isValid)}
            helperText={
              (endTime && !rangeIsValid.isValid && rangeIsValid.error) || ""
            }
            disabled={allDay}
          />
        </Grid>
      </Grid>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginTop={2}
      >
        <Typography>Repeat</Typography>
        <FormControl>
          <RepeatSelect
            values={values}
            handleChange={handleChange}
            handleFocus={handleRepeatFocus}
          />
        </FormControl>
      </Box>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          id="unavailable-form-fields-notes"
          label="Notes"
          name="notes"
          variant="outlined"
          value={values?.notes || ""}
          onChange={handleChange}
          onClick={handleNotesClick}
        />
      </Grid>
    </Fragment>
  );
};
export default UnavailableFormFields;
